import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, TextArea } from '../../../shared/ui'
import { useDecideProbearbeiter, useProbearbeiterQueue, type ProbearbeiterRow } from './onboardingApi'
import { renderRejectionEmail } from './emailTemplates'

function StarsLine({ value }: { value: number | null }) {
  if (value == null) return <span className="text-on-surface-variant/60">–</span>
  return <span className="text-amber-400">{'★'.repeat(value)}<span className="text-on-surface-variant/40">{'☆'.repeat(5 - value)}</span></span>
}

export function ProbearbeiterReviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queueQ = useProbearbeiterQueue()
  const decide = useDecideProbearbeiter()

  const row: ProbearbeiterRow | undefined = useMemo(
    () => queueQ.data?.rows.find(r => String(r.main_id) === id),
    [queueQ.data, id],
  )

  const [showReject, setShowReject] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [hrName, setHrName] = useState('Mammas Canteen HR')
  const [emailWasSent, setEmailWasSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (queueQ.isPending) return <div className="p-6">Lade…</div>
  if (!row) return (
    <div className="p-6">
      <p>Probearbeiter nicht gefunden oder bereits entschieden.</p>
      <Button onClick={() => navigate('/hr/onboarding')}>Zurück zur Übersicht</Button>
    </div>
  )

  const tpl = renderRejectionEmail({
    vorname: row.vorname,
    nachname: row.nachname,
    schule: row.probe_schule_name ?? null,
    probeVon: row.probe_von_datum,
    probeBis: row.probe_bis_datum,
    hrName,
    rejectionReason,
  })
  const mailtoHref = `mailto:${encodeURIComponent(row.email)}?subject=${encodeURIComponent(tpl.subject)}&body=${encodeURIComponent(tpl.body)}`

  async function onAccept() {
    setError(null)
    try {
      await decide.mutateAsync({ mainId: row!.main_id, decision: 'accept' })
      navigate(`/hr/onboarding/wizard/${row!.main_id}`)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Annahme fehlgeschlagen')
    }
  }
  async function onReject() {
    setError(null)
    try {
      await decide.mutateAsync({
        mainId: row!.main_id,
        decision: 'reject',
        rejectionReason: rejectionReason.trim() || undefined,
        emailWasSent,
      })
      navigate('/hr/onboarding?rejected=' + row!.main_id)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Ablehnung fehlgeschlagen')
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).catch(() => { /* ignore */ })
    setEmailWasSent(true)
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-8 space-y-6">
      <header>
        <button onClick={() => navigate('/hr/onboarding')} className="text-sm text-on-surface-variant hover:text-on-surface mb-2">
          ← Zurück zur Übersicht
        </button>
        <h1 className="text-2xl font-semibold">{row.vorname} {row.nachname}</h1>
        <p className="text-sm text-on-surface-variant mt-1">
          Probearbeit {row.probe_schule_name ?? '—'} · {row.probe_von_datum} bis {row.probe_bis_datum}
          {row.probe_stunden_gesamt != null && <> · {row.probe_stunden_gesamt}h gesamt</>}
        </p>
        <p className="text-sm text-on-surface-variant">
          E-Mail: <a className="underline" href={`mailto:${row.email}`}>{row.email}</a>
          {row.handy && <> · {row.handy}</>}
        </p>
        <p className="text-xs text-on-surface-variant/70 mt-1">Eingegangen am {row.ingress_at} von {row.ingress_by}</p>
      </header>

      <section className="rounded-2xl bg-surface-container/40 border border-outline-variant/30 p-4 space-y-3">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">Bewertung</h2>
        <div className="grid grid-cols-2 gap-y-1.5 text-sm">
          <div>Pünktlichkeit</div><div><StarsLine value={row.probe_rating_punktlichkeit ?? null} /></div>
          <div>Hygiene</div><div><StarsLine value={row.probe_rating_hygiene ?? null} /></div>
          <div>Teamfähigkeit</div><div><StarsLine value={row.probe_rating_teamfaehigkeit ?? null} /></div>
          <div>Engagement</div><div><StarsLine value={row.probe_rating_engagement ?? null} /></div>
          {row.probe_empfehlung && (
            <>
              <div>Empfehlung des PL</div>
              <div className="font-medium">
                {row.probe_empfehlung === 'ja' ? '✓ Ja' : row.probe_empfehlung === 'nein' ? '✗ Nein' : '~ Vielleicht'}
              </div>
            </>
          )}
        </div>
        {row.probe_notizen && (
          <div className="pt-2 border-t border-outline-variant/30">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant mb-1">Notizen</div>
            <p className="text-sm whitespace-pre-wrap">{row.probe_notizen}</p>
          </div>
        )}
      </section>

      {!showReject ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => setShowReject(true)} disabled={decide.isPending}>
            ✗ Ablehnen
          </Button>
          <Button variant="primary" onClick={onAccept} disabled={decide.isPending}>
            ✓ Annehmen — Onboarding starten
          </Button>
        </div>
      ) : (
        <section className="rounded-2xl bg-surface-container/40 border border-outline-variant/30 p-4 space-y-4">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">Ablehnung</h2>
          <p className="text-sm text-on-surface-variant">
            Die E-Mail wird nicht automatisch versendet. Sie können den Text unten anpassen, in die Zwischenablage kopieren
            und in Ihrem E-Mail-Programm versenden. Empfänger: <strong>{row.email}</strong>.
          </p>

          <TextInputLine label="Ihr Name (Absender:in)" value={hrName} onChange={setHrName} />

          <TextArea variant="glass"
            label="Optionale Begründung (frei)"
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            rows={3}
            placeholder={'Z. B. „Aktuell keine passende Stelle frei." — bleibt leer = neutral formulierte Absage.'}
          />

          <div className="rounded-xl bg-surface/60 border border-outline-variant/40 p-3 text-xs space-y-2">
            <div className="font-medium text-on-surface-variant">Vorschau (Betreff + Body)</div>
            <div>
              <div className="text-on-surface-variant text-[10px] uppercase tracking-wider">Betreff</div>
              <div className="text-sm">{tpl.subject}</div>
            </div>
            <div>
              <div className="text-on-surface-variant text-[10px] uppercase tracking-wider">Body</div>
              <pre className="text-sm whitespace-pre-wrap font-sans">{tpl.body}</pre>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => copyToClipboard(`Betreff: ${tpl.subject}\n\n${tpl.body}`)}>
                In Zwischenablage kopieren
              </Button>
              <a className="inline-flex items-center text-sm underline text-primary" href={mailtoHref}>
                E-Mail-Programm öffnen
              </a>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={emailWasSent} onChange={e => setEmailWasSent(e.target.checked)} />
            Ich habe die Absage-E-Mail in meinem E-Mail-Programm versendet
          </label>

          {error && (
            <div className="rounded-xl border border-error/40 bg-error-container/30 px-4 py-3 text-sm text-on-error-container">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowReject(false)} disabled={decide.isPending}>
              Zurück
            </Button>
            <Button variant="primary" onClick={onReject} disabled={decide.isPending}>
              Ablehnung speichern
            </Button>
          </div>
        </section>
      )}
    </div>
  )
}

// Tiny inline single-line text input (avoids re-importing TextInput just for one line).
function TextInputLine({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] font-medium uppercase tracking-wider text-on-surface-variant">{label}</label>
      <input
        className="w-full rounded-xl bg-surface-container/40 border border-outline-variant px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  )
}
